/**
 * Self-contained review modal for a single proposed edit. Used by both
 * the full-page list (`list.tsx`) and the inline panel that lives on
 * funder/opportunity show pages (`ProposedEditsPanel.tsx`), so admins
 * see the same diff / amend / questions-editor / approve-reject UI in
 * both places.
 *
 * Manages its own state (chain fetch, current-record fetch, review,
 * amend). Parent provides the edit and an `onChanged` callback to
 * refetch its own list after a successful approve / reject / amend.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import axios from 'axios'
import { Link as RouterLink } from 'react-router-dom'

import {
  apiUrl,
  type DiffRow,
  type ProposedEdit,
  type ResourceType,
  buildDiff,
  buildFundingRoundBefore,
  diffColumnLabels,
  fetchCurrent,
  fetchDispositionPreviews,
  fetchMergePreview,
  type MergePreview,
  fieldDefFor,
  formatRegionValue,
  labelFor,
  questionsFromPayload,
  regionIdsFromValue,
  statusColor,
  targetDisplay,
  targetHref,
  validatePayload,
} from './_shared'
import {
  AmendForm,
  QuestionsAmendForm,
  QuestionsPreview,
  renderDiffCell,
  SafeExternalLink,
} from './_shared-components'
import { AdvisoryDetail } from './AdvisoryDetail'
import { RegenPanel } from './RegenPanel'
import { FunderRegenPanel } from './FunderRegenPanel'
import { isRegenerableOpportunity } from '@/utils/opportunityRegen'
import { MergeSummary } from './MergeSummary'
import { RoundDeleteSummary } from './RoundDeleteSummary'
import { formatLongDateNZ } from '../../utils/formatDate'

export interface EditReviewModalProps {
  /** The edit to review. `null` closes the modal. */
  edit: ProposedEdit | null
  /** Called when the user closes the modal (Close button or backdrop click). */
  onClose: () => void
  /**
   * Called after a successful approve / reject / amend so the parent can
   * refetch its list. Not called for view-only opens.
   */
  onChanged?: () => void | Promise<void>
}

interface ApprovalConflict {
  field: string
  database_value: unknown
  proposed_value: unknown
}

export const EditReviewModal: React.FC<EditReviewModalProps> = ({
  edit,
  onClose,
  onChanged,
}) => {
  // The modal can navigate within a rework chain via the "Open" button on
  // a sibling chain entry — keep that as local state so the parent only
  // needs to know about the originally-opened edit.
  const [activeEdit, setActiveEdit] = useState<ProposedEdit | null>(edit)
  useEffect(() => {
    // Sync from parent when:
    //  - we have no active edit yet, OR
    //  - the parent's edit ID matches what we're currently showing
    //    (covers refresh-after-amend, status updates, payload tweaks).
    // If the user has navigated to a sibling in the rework chain, leave
    // them there even when the parent's `edit` object refreshes.
    setActiveEdit((prev) => {
      if (!edit) return null
      if (!prev || prev.id === edit.id) return edit
      return prev
    })
  }, [edit])

  const [chain, setChain] = useState<ProposedEdit[]>([])
  const [currentRecord, setCurrentRecord] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [loadingCurrent, setLoadingCurrent] = useState(false)
  const [loadingCurrentRegions, setLoadingCurrentRegions] = useState(false)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewing, setReviewing] = useState(false)
  const [amendingId, setAmendingId] = useState<number | null>(null)
  const [amendDraft, setAmendDraft] = useState<Record<string, unknown>>({})
  const [amendSaving, setAmendSaving] = useState(false)
  const [currentRegionIds, setCurrentRegionIds] = useState<number[] | null>(
    null,
  )
  const [funderRegionIds, setFunderRegionIds] = useState<number[]>([])
  const [regionNamesById, setRegionNamesById] = useState<Map<number, string>>(
    () => new Map(),
  )
  // GH#1630: true while a regen panel holds generated/edited profile+tags the
  // reviewer has not saved or discarded. Blocks Approve/Reject so a stray click
  // can't lose that work.
  const [regenUnsaved, setRegenUnsaved] = useState(false)
  // GH#1849 P1: approve-button gating. On an edit whose diff warrants a profile
  // regen, Approve is disabled from the outset; it unlocks only when the reviewer
  // regenerates-and-saves (a panel reports completed) OR clicks "Skip profile
  // regeneration". A decision is stored as a *gate key* = `${edit id}:${driver
  // fingerprint}` (NOT a bare id) and compared against the current key, so it is
  // inert both when the edit changes AND when the reviewer amends the edit's
  // driver fields (description/criteria/exclusions) — an amend after a regen/skip
  // re-arms the gate, since the amended content was never regenerated.
  const [regenCompletedKey, setRegenCompletedKey] = useState<string | null>(
    null,
  )
  const [regenSkippedKey, setRegenSkippedKey] = useState<string | null>(null)
  // Active opps under the target funder (the backend advisory emit-set), keyed
  // by edit + funder so a count from the previous edit never reads as known.
  // 'error' falls back to gating so the reviewer can still Skip.
  const [funderOppCount, setFunderOppCount] = useState<{
    key: string
    count: number | 'error'
  } | null>(null)
  const funderOppCountKey = activeEdit
    ? `${activeEdit.id}:${activeEdit.target_uuid}`
    : null
  const funderActiveOppCount =
    funderOppCount && funderOppCount.key === funderOppCountKey
      ? funderOppCount.count
      : null
  const [error, setError] = useState<string | null>(null)
  const errorAlertRef = useRef<HTMLDivElement>(null)
  const [approvalConflicts, setApprovalConflicts] = useState<
    ApprovalConflict[]
  >([])

  // GH review follow-up: a review-request failure (e.g. the 400 from the
  // next_check_in_date invariant) previously only appeared as plain text at
  // the top of a potentially long modal -- easy to miss below the fold, and
  // silent to screen readers. Move focus/scroll to it whenever it appears.
  useEffect(() => {
    if (!error || !errorAlertRef.current) return
    // jsdom (used in tests) doesn't implement scrollIntoView.
    if (typeof errorAlertRef.current.scrollIntoView === 'function') {
      errorAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    errorAlertRef.current.focus()
  }, [error])

  // Reset transient state when the modal switches to a different edit or closes.
  useEffect(() => {
    setReviewComment('')
    setAmendingId(null)
    setRegenUnsaved(false)
    setRegenCompletedKey(null)
    setRegenSkippedKey(null)
    setAmendDraft({})
    setError(null)
    setApprovalConflicts([])
    setChain([])
    setCurrentRecord(null)
    setCurrentRegionIds(null)
    setLoadingCurrent(false)
    setLoadingCurrentRegions(false)
  }, [activeEdit?.id])

  // Fetch the rework chain whenever an edit is shown.
  useEffect(() => {
    if (!activeEdit) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get<ProposedEdit[]>(
          `${apiUrl}/proposed-edits/${activeEdit.id}/chain`,
        )
        if (!cancelled) setChain(res.data)
      } catch (e) {
        console.warn(`Failed to fetch chain for edit #${activeEdit.id}`, e)
        if (!cancelled) setChain([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeEdit?.id])

  // Fetch the current record (for the diff "before" column) for update edits.
  // funding_round updates use the inline target_round_* fields instead.
  useEffect(() => {
    if (!activeEdit) return
    if (activeEdit.action !== 'update') return
    if (activeEdit.resource_type === 'advisory') return
    if (activeEdit.resource_type === 'funding_round') {
      setCurrentRecord(buildFundingRoundBefore(activeEdit))
      return
    }
    if (!activeEdit.target_uuid) return
    let cancelled = false
    setLoadingCurrent(true)
    ;(async () => {
      const data = await fetchCurrent(
        activeEdit.resource_type,
        activeEdit.target_uuid as string,
      )
      if (!cancelled) {
        setCurrentRecord(data)
        setLoadingCurrent(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    activeEdit?.id,
    activeEdit?.target_uuid,
    activeEdit?.action,
    activeEdit?.resource_type,
  ])

  // A funder description edit only warrants a regen when the funder has active
  // opps to regenerate, so count them before the gate decides.
  useEffect(() => {
    if (
      activeEdit?.status !== 'pending' ||
      activeEdit.resource_type !== 'funder' ||
      !activeEdit.target_uuid
    )
      return
    let cancelled = false
    const key = `${activeEdit.id}:${activeEdit.target_uuid}`
    axios.get(`${apiUrl}/fs/funders/${activeEdit.target_uuid}`).then(
      (res) => {
        if (cancelled) return
        const opps: Array<{ flag_for_updates: string | null }> =
          res.data?.opportunities ?? []
        setFunderOppCount({
          key,
          count: opps.filter(isRegenerableOpportunity).length,
        })
      },
      () => {
        if (!cancelled) setFunderOppCount({ key, count: 'error' })
      },
    )
    return () => {
      cancelled = true
    }
  }, [
    activeEdit?.id,
    activeEdit?.target_uuid,
    activeEdit?.resource_type,
    activeEdit?.status,
  ])

  // A merge's impact is a live read of both records, so it is only available
  // while the merge is pending: approving deletes the record it describes.
  const [mergePreview, setMergePreview] = useState<MergePreview | null>(null)
  const [dispositionPreviews, setDispositionPreviews] = useState<
    { oppUuid: string; preview: MergePreview }[]
  >([])
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false)
  const [mergePreviewFailed, setMergePreviewFailed] = useState(false)
  useEffect(() => {
    setMergePreview(null)
    setDispositionPreviews([])
    setMergePreviewFailed(false)
    setMergePreviewLoading(false)
    if (!activeEdit) return
    if (activeEdit.action !== 'merge') return
    if (activeEdit.status !== 'pending') return
    let cancelled = false
    setMergePreviewLoading(true)
    ;(async () => {
      try {
        const [preview, dispositions] = await Promise.all([
          fetchMergePreview(activeEdit),
          fetchDispositionPreviews(activeEdit),
        ])
        if (!cancelled) {
          setMergePreview(preview)
          setDispositionPreviews(dispositions)
        }
      } catch (e) {
        console.error('Failed to read the merge impact', e)
        if (!cancelled) setMergePreviewFailed(true)
      } finally {
        if (!cancelled) setMergePreviewLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeEdit?.id, activeEdit?.action, activeEdit?.status])

  /**
   * Approving a merge cannot be undone, so it is allowed only once the impact
   * has been read and shows nothing blocking. A failed read is treated as a
   * blocker: the reviewer is being asked to approve a deletion sight unseen.
   */
  const mergeApprovalBlocked = useMemo(() => {
    if (activeEdit?.action !== 'merge') return false
    if (activeEdit.status !== 'pending') return false
    if (mergePreviewLoading || mergePreviewFailed) return true
    // an impact without a blocker list is one that cannot be confirmed clear
    if (!Array.isArray(mergePreview?.blockers)) return true
    if (mergePreview.blockers.length > 0) return true
    return dispositionPreviews.some((d) => d.preview?.blockers?.length)
  }, [
    activeEdit?.action,
    activeEdit?.status,
    mergePreview,
    mergePreviewLoading,
    mergePreviewFailed,
    dispositionPreviews,
  ])

  const needsCurrentRegionLookup = useMemo(() => {
    if (!activeEdit) return false
    if (activeEdit.action !== 'update') return false
    if (!activeEdit.target_uuid) return false
    if (
      activeEdit.resource_type !== 'funder' &&
      activeEdit.resource_type !== 'opportunity'
    ) {
      return false
    }
    return Object.prototype.hasOwnProperty.call(
      activeEdit.payload ?? {},
      'region_ids',
    )
  }, [activeEdit])

  useEffect(() => {
    if (!activeEdit || !needsCurrentRegionLookup || !activeEdit.target_uuid) {
      setCurrentRegionIds(null)
      setLoadingCurrentRegions(false)
      return
    }

    let cancelled = false
    const regionResource =
      activeEdit.resource_type === 'funder'
        ? 'funder-regions'
        : 'opportunity-regions'
    setCurrentRegionIds(null)
    setLoadingCurrentRegions(true)
    ;(async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/${regionResource}/${activeEdit.target_uuid}`,
        )
        if (cancelled) return
        setCurrentRegionIds(regionIdsFromValue(res.data))
      } catch (e) {
        console.error('Failed to fetch current regions for diff display', e)
        if (!cancelled) setCurrentRegionIds(null)
      } finally {
        if (!cancelled) setLoadingCurrentRegions(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    activeEdit?.id,
    activeEdit?.resource_type,
    activeEdit?.target_uuid,
    apiUrl,
    needsCurrentRegionLookup,
  ])

  const currentRecordForDiff = useMemo(() => {
    if (!currentRecord) return currentRecord
    if (!needsCurrentRegionLookup) return currentRecord
    if (currentRegionIds === null) return currentRecord
    return {
      ...currentRecord,
      region_ids: currentRegionIds,
    }
  }, [currentRecord, currentRegionIds, needsCurrentRegionLookup])

  const diffRows = useMemo<DiffRow[]>(() => {
    if (!activeEdit || activeEdit.resource_type === 'advisory') return []
    if (activeEdit.action === 'merge') return []
    if (
      activeEdit.resource_type === 'funding_round' &&
      activeEdit.action === 'delete'
    )
      return []
    const before = activeEdit.action === 'update' ? currentRecordForDiff : null
    return buildDiff(before, activeEdit.payload, activeEdit.resource_type)
  }, [activeEdit, currentRecordForDiff])

  const expectedValues = useMemo<Record<string, unknown>>(() => {
    if (!activeEdit || activeEdit.action !== 'update' || !currentRecordForDiff)
      return {}
    const values = Object.fromEntries(
      Object.keys(activeEdit.payload)
        .map((key) => [
          key,
          activeEdit.resource_type === 'opportunity_questions' &&
          key === 'questions'
            ? // Mirror the server: it shadows the raw `questions` column with
              // the structured rows only when structured rows exist. Sending
              // an empty structured list for a raw-only target made untouched
              // records conflict against themselves (GH#2070).
              Array.isArray(currentRecordForDiff.structured_questions) &&
              currentRecordForDiff.structured_questions.length > 0
              ? currentRecordForDiff.structured_questions
              : currentRecordForDiff.questions
            : currentRecordForDiff[key],
        ])
        .filter(([, value]) => value !== undefined),
    )
    if (activeEdit.payload.clear_next_check_in_date === true) {
      values.next_check_in_date = currentRecordForDiff.next_check_in_date
    }
    if (activeEdit.payload.clear_questions_last_checked_at === true) {
      values.questions_last_checked_at =
        currentRecordForDiff.questions_last_checked_at
    }
    if ('inherit_iwi_from_funder' in activeEdit.payload) {
      values.iwi = currentRecordForDiff.iwi
    }
    return values
  }, [activeEdit, currentRecordForDiff])

  const regionRows = useMemo(
    () => diffRows.filter((row) => row.key === 'region_ids'),
    [diffRows],
  )

  const needsInheritedRegionDisplay = useMemo(() => {
    if (!activeEdit || activeEdit.resource_type !== 'opportunity') return false
    return regionRows.some(
      (row) =>
        (Array.isArray(row.before) &&
          regionIdsFromValue(row.before).length === 0) ||
        (Array.isArray(row.after) &&
          regionIdsFromValue(row.after).length === 0),
    )
  }, [activeEdit, regionRows])

  const funderUuidForRegions = useMemo(() => {
    if (!activeEdit || activeEdit.resource_type !== 'opportunity') return null
    const payloadFunderUuid = activeEdit.payload.funder_uuid
    const currentFunderUuid = currentRecordForDiff?.funder_uuid
    return String(
      activeEdit.target_funder_uuid ||
        currentFunderUuid ||
        payloadFunderUuid ||
        '',
    )
  }, [activeEdit, currentRecordForDiff])

  // GH#1849 P1: approve-button gating. An edit whose diff warrants a profile
  // regen must be regenerated-and-saved (or explicitly skipped) before Approve
  // unlocks. These conditions are EXACTLY congruent with the RegenPanel /
  // FunderRegenPanel render conditions below (same resource_type + target_uuid +
  // driver-field checks), so the gate can never demand a regen with no panel to
  // satisfy it. Reject is intentionally NOT gated — rejecting applies no profile.
  const oppRegenWarranted =
    activeEdit?.resource_type === 'opportunity' &&
    !!activeEdit.target_uuid &&
    diffRows.some((r) =>
      ['description', 'criteria', 'exclusions'].includes(r.key),
    )
  const funderRegenWarranted =
    activeEdit?.resource_type === 'funder' &&
    !!activeEdit.target_uuid &&
    diffRows.some((r) => r.key === 'description') &&
    funderActiveOppCount !== null &&
    funderActiveOppCount !== 0
  const regenWarranted =
    activeEdit?.status === 'pending' &&
    (oppRegenWarranted || funderRegenWarranted)

  // Fingerprint of the driver fields the reviewer must regenerate. Amending any
  // of them changes the fingerprint (and thus the gate key), so a prior
  // regen/skip decision no longer applies — the gate re-arms.
  const driverFingerprint = useMemo(() => {
    const p = activeEdit?.payload ?? {}
    if (activeEdit?.resource_type === 'opportunity') {
      return JSON.stringify([
        p.description ?? null,
        p.criteria ?? null,
        p.exclusions ?? null,
      ])
    }
    if (activeEdit?.resource_type === 'funder') {
      return JSON.stringify([p.description ?? null])
    }
    return ''
  }, [activeEdit])
  const gateKey = activeEdit ? `${activeEdit.id}:${driverFingerprint}` : null
  // A ref so the completion callback stays referentially stable (useCallback
  // with [] deps) yet always records the CURRENT gate key — avoids the panel's
  // effect re-firing every render and re-stamping the key after an amend.
  const gateKeyRef = useRef<string | null>(gateKey)
  gateKeyRef.current = gateKey
  const handleRegenCompleted = useCallback((completed: boolean) => {
    setRegenCompletedKey(completed ? gateKeyRef.current : null)
  }, [])

  const regenCompleted = !!gateKey && regenCompletedKey === gateKey
  const regenSkipped = !!gateKey && regenSkippedKey === gateKey
  // The confirmed gate: a warranting diff that hasn't been regenerated or
  // skipped. Drives the Skip button + hint banner.
  const regenGateActive = !!regenWarranted && !regenCompleted && !regenSkipped
  // While the current record (or a funder's active-opp count) is still
  // loading, a driver change hasn't resolved yet — keep Approve disabled for any pending opp/funder edit
  // that COULD warrant a regen so it never flashes enabled before the diff
  // loads. (Approve only; no Skip/banner flash until the gate is confirmed.)
  const regenMaybePendingWhileLoading =
    activeEdit?.status === 'pending' &&
    (activeEdit.resource_type === 'opportunity' ||
      activeEdit.resource_type === 'funder') &&
    !!activeEdit.target_uuid &&
    (loadingCurrent ||
      (activeEdit.resource_type === 'funder' && funderActiveOppCount === null))
  const approveBlockedByRegen =
    regenGateActive || !!regenMaybePendingWhileLoading

  useEffect(() => {
    if (!needsInheritedRegionDisplay || !funderUuidForRegions) {
      setFunderRegionIds([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/funder-regions/${funderUuidForRegions}`,
        )
        if (cancelled) return
        setFunderRegionIds(regionIdsFromValue(res.data))
      } catch (e) {
        console.error('Failed to fetch funder regions for diff display', e)
        if (!cancelled) setFunderRegionIds([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [funderUuidForRegions, needsInheritedRegionDisplay])

  const regionIdsForNameLookup = useMemo(() => {
    const ids = new Set<number>()
    regionRows.forEach((row) => {
      regionIdsFromValue(row.before).forEach((id) => ids.add(id))
      regionIdsFromValue(row.after).forEach((id) => ids.add(id))
    })
    funderRegionIds.forEach((id) => ids.add(id))
    return Array.from(ids)
  }, [funderRegionIds, regionRows])

  useEffect(() => {
    if (regionIdsForNameLookup.length === 0) {
      setRegionNamesById(new Map())
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get(`${apiUrl}/regions/`, {
          params: {
            current: 1,
            pageSize: Math.max(regionIdsForNameLookup.length, 1),
            // The admin regions API treats an array filter value as an id IN lookup.
            filters: JSON.stringify([
              {
                field: 'id',
                operator: 'eq',
                value: regionIdsForNameLookup,
              },
            ]),
          },
        })
        if (cancelled) return
        const needed = new Set(regionIdsForNameLookup)
        const regions = Array.isArray(res.data) ? res.data : []
        setRegionNamesById(
          new Map(
            regions
              .filter((region) => needed.has(Number(region.id)))
              .map((region) => [Number(region.id), String(region.name)]),
          ),
        )
      } catch (e) {
        console.error('Failed to fetch region names for diff display', e)
        if (!cancelled) setRegionNamesById(new Map())
      }
    })()
    return () => {
      cancelled = true
    }
  }, [regionIdsForNameLookup])

  const regionDiffFormatter = useCallback(
    (value: unknown) =>
      formatRegionValue(
        value,
        regionNamesById,
        activeEdit?.resource_type === 'opportunity' ? funderRegionIds : [],
      ),
    [activeEdit?.resource_type, funderRegionIds, regionNamesById],
  )

  const startAmend = useCallback((item: ProposedEdit) => {
    setAmendingId(item.id)
    setAmendDraft({ ...item.payload })
  }, [])

  const cancelAmend = useCallback(() => {
    setAmendingId(null)
    setAmendDraft({})
  }, [])

  const saveAmend = useCallback(
    async (item: ProposedEdit) => {
      setAmendSaving(true)
      try {
        // Amendments no longer carry their own comment (GH#1592): reviewers use
        // the modal's review-comment box instead. The endpoint still accepts an
        // optional `comment`, we just stop sending one from the UI.
        await axios.patch(`${apiUrl}/proposed-edits/${item.id}`, {
          payload: amendDraft,
        })
        cancelAmend()
        await onChanged?.()
      } catch (e: unknown) {
        const err = e as {
          response?: { status?: number; data?: { detail?: string } }
          message?: string
        }
        const status = err?.response?.status
        const detail = err?.response?.data?.detail
        if (status === 409) {
          cancelAmend()
          setError(detail ?? 'This edit was reviewed before your amend landed.')
          await onChanged?.()
        } else {
          setError(detail ?? err?.message ?? 'Amend failed')
        }
      } finally {
        setAmendSaving(false)
      }
    },
    [amendDraft, cancelAmend, onChanged],
  )

  const review = useCallback(
    async (
      action: 'approve' | 'reject',
      conflictResolution?: 'database' | 'proposed',
    ) => {
      if (!activeEdit) return
      setReviewing(true)
      try {
        const body: Record<string, unknown> = {}
        if (reviewComment) body.comment = reviewComment
        if (action === 'approve' && Object.keys(expectedValues).length > 0) {
          body.expected_values = conflictResolution
            ? {
                ...expectedValues,
                ...Object.fromEntries(
                  approvalConflicts.map((conflict) => [
                    conflict.field,
                    conflict.database_value,
                  ]),
                ),
              }
            : expectedValues
        }
        if (conflictResolution) {
          body.conflict_resolution = conflictResolution
          body.conflict_fields = approvalConflicts.map(
            (conflict) => conflict.field,
          )
        }
        await axios.post(
          `${apiUrl}/proposed-edits/${activeEdit.id}/${action}`,
          body,
        )
        onClose()
        await onChanged?.()
      } catch (e: unknown) {
        const err = e as {
          response?: {
            status?: number
            data?: {
              detail?:
                | string
                | {
                    code?: string
                    message?: string
                    conflicts?: ApprovalConflict[]
                  }
            }
          }
          message?: string
        }
        const status = err?.response?.status
        const detail = err?.response?.data?.detail
        if (status === 409) {
          if (
            typeof detail === 'object' &&
            detail?.code === 'proposed_edit_data_conflict' &&
            Array.isArray(detail.conflicts)
          ) {
            setApprovalConflicts(detail.conflicts)
            setError(null)
            return
          }
          // GH#1622: keep the modal OPEN so the error stays visible — calling
          // onClose() first unmounts the dialog and the inline error below is
          // never seen. 409 here means the edit could not be applied as-is:
          // another admin got here first, or the create duplicates an existing
          // opportunity. Surface the server detail, refresh the parent so the
          // row shows its true state, and let the reviewer dismiss the modal.
          setError(
            typeof detail === 'string'
              ? detail
              : 'This edit was already reviewed by another admin.',
          )
          await onChanged?.()
        } else {
          setError(
            (typeof detail === 'string' ? detail : detail?.message) ??
              err?.message ??
              'Review failed',
          )
        }
      } finally {
        setReviewing(false)
      }
    },
    [
      activeEdit,
      reviewComment,
      expectedValues,
      approvalConflicts,
      onClose,
      onChanged,
    ],
  )

  return (
    <Dialog
      open={!!activeEdit}
      onClose={(_event, _reason) => {
        // GH review follow-up: don't let Escape/backdrop dismiss the modal
        // while a review request is in flight -- the eventual failure would
        // otherwise land on a closed/unmounted surface with no way to show it.
        if (reviewing) return
        onClose()
      }}
      maxWidth="lg"
      fullWidth
    >
      {activeEdit && (
        <>
          <DialogTitle>
            Proposed edit #{activeEdit.id} — {activeEdit.resource_type} /{' '}
            {activeEdit.action}
          </DialogTitle>
          <DialogContent dividers>
            {error && (
              <Alert
                severity="error"
                role="alert"
                ref={errorAlertRef}
                tabIndex={-1}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}
            {approvalConflicts.length > 0 && (
              <Paper
                variant="outlined"
                sx={{ mb: 2, p: 2, borderColor: 'warning.main' }}
              >
                <Typography variant="subtitle1" color="warning.dark">
                  Data conflict between review queue proposed edit and database
                  changes made since. Which version would you like to keep?
                </Typography>
                <TableContainer sx={{ mt: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Database version</TableCell>
                        <TableCell>Proposed edit version</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {approvalConflicts.map((conflict) => {
                        const normalDiff = diffRows.find(
                          (row) => row.key === conflict.field,
                        )
                        const formatter =
                          fieldDefFor(activeEdit.resource_type, conflict.field)
                            .type === 'date'
                            ? formatLongDateNZ
                            : undefined
                        return (
                          <TableRow key={conflict.field}>
                            <TableCell>
                              {/* Fields the main diff folds into a synthetic
                                  row (the structured amount range) have no
                                  matching diffRow, so fall back to the shared
                                  label map rather than the raw column name. */}
                              {normalDiff?.label ?? labelFor(conflict.field)}
                            </TableCell>
                            <TableCell>
                              {renderDiffCell(
                                conflict.database_value,
                                conflict.proposed_value,
                                'before',
                                true,
                                formatter,
                              )}
                            </TableCell>
                            <TableCell>
                              {renderDiffCell(
                                conflict.database_value,
                                conflict.proposed_value,
                                'after',
                                true,
                                formatter,
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => review('approve', 'database')}
                    disabled={reviewing}
                  >
                    Keep database version
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => review('approve', 'proposed')}
                    disabled={reviewing}
                  >
                    Keep proposed edit version
                  </Button>
                </Box>
              </Paper>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2">Status</Typography>
                <Chip
                  label={activeEdit.status}
                  color={statusColor[activeEdit.status]}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2">Submitted by</Typography>
                <Typography variant="body2">
                  {activeEdit.submitted_by_name ??
                    activeEdit.submitted_by_auth0_id}
                  {activeEdit.api_key_id != null &&
                    ` (api_key #${activeEdit.api_key_id})`}
                </Typography>
              </Box>
              {(activeEdit.target_uuid || activeEdit.target_name) && (
                <Box sx={{ gridColumn: '1 / span 2' }}>
                  <Typography variant="subtitle2">Target</Typography>
                  {(() => {
                    const href = targetHref(activeEdit)
                    const label =
                      activeEdit.target_name ?? targetDisplay(activeEdit)
                    const uuidCaption =
                      activeEdit.applied_target_uuid ?? activeEdit.target_uuid
                    return (
                      <>
                        <Typography variant="body2">
                          {href ? (
                            <MuiLink
                              component={RouterLink}
                              to={href}
                              underline="hover"
                            >
                              {label}
                            </MuiLink>
                          ) : (
                            label
                          )}
                          {activeEdit.resource_type !== 'funder' &&
                            activeEdit.target_funder_name && (
                              <>
                                {' · '}
                                {activeEdit.target_funder_uuid ? (
                                  <MuiLink
                                    component={RouterLink}
                                    to={`/funders/${activeEdit.target_funder_uuid}`}
                                    underline="hover"
                                  >
                                    {activeEdit.target_funder_name}
                                  </MuiLink>
                                ) : (
                                  activeEdit.target_funder_name
                                )}
                              </>
                            )}
                        </Typography>
                        {uuidCaption && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: 11,
                              color: 'text.secondary',
                            }}
                          >
                            {uuidCaption}
                          </Typography>
                        )}
                      </>
                    )
                  })()}
                </Box>
              )}
              {activeEdit.note && (
                <Box sx={{ gridColumn: '1 / span 2' }}>
                  <Typography variant="subtitle2">Submitter note</Typography>
                  <Typography variant="body2">{activeEdit.note}</Typography>
                </Box>
              )}
              {chain.length > 1 && (
                <Box sx={{ gridColumn: '1 / span 2' }}>
                  <Typography variant="subtitle2">
                    Edit thread ({chain.length} edits)
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {chain.map((entry) => {
                      return (
                        <Box
                          key={entry.id}
                          sx={{
                            border: '1px solid',
                            borderColor:
                              entry.id === activeEdit.id
                                ? 'primary.main'
                                : 'divider',
                            borderRadius: 1,
                            p: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              #{entry.id}
                            </Typography>
                            <Chip
                              label={entry.status}
                              size="small"
                              color={statusColor[entry.status]}
                            />
                            <Typography variant="caption">
                              {entry.resource_type}.{entry.action} •{' '}
                              {new Date(entry.created_at + 'Z').toLocaleString(
                                'en-NZ',
                              )}
                            </Typography>
                            {entry.responds_to_edit_id != null && (
                              <Typography variant="caption">
                                responds to #{entry.responds_to_edit_id}
                              </Typography>
                            )}
                            {entry.id !== activeEdit.id && (
                              <Button
                                size="small"
                                onClick={() => setActiveEdit(entry)}
                              >
                                Open
                              </Button>
                            )}
                          </Box>
                          {entry.note && (
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                            >
                              <strong>
                                submitter{' '}
                                {entry.submitted_by_name
                                  ? `(${entry.submitted_by_name})`
                                  : ''}
                                :{' '}
                              </strong>
                              {entry.note}
                            </Typography>
                          )}
                          {entry.review_comment && (
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                            >
                              <strong>
                                admin{' '}
                                {entry.reviewed_by_name
                                  ? `(${entry.reviewed_by_name})`
                                  : ''}
                                :{' '}
                              </strong>
                              {entry.review_comment}
                            </Typography>
                          )}
                          {entry.withdraw_reason && (
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                            >
                              <strong>withdrawal reason: </strong>
                              {entry.withdraw_reason}
                            </Typography>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )}
              <Box sx={{ gridColumn: '1 / span 2' }}>
                <Typography variant="subtitle2">Evidence</Typography>
                <Typography variant="body2">
                  <SafeExternalLink
                    value={activeEdit.evidence_url}
                    underline="hover"
                  />
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                >
                  {activeEdit.evidence_description}
                </Typography>
              </Box>
              {activeEdit.batch_id && (
                <Box sx={{ gridColumn: '1 / span 2' }}>
                  <Typography variant="subtitle2">Batch</Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                  >
                    {activeEdit.batch_id}
                  </Typography>
                </Box>
              )}
            </Box>

            {activeEdit.resource_type === 'advisory' ? (
              <AdvisoryDetail item={activeEdit} />
            ) : activeEdit.action === 'merge' ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Merge
                </Typography>
                <MergeSummary
                  item={activeEdit}
                  preview={mergePreview}
                  dispositionPreviews={dispositionPreviews}
                  previewLoading={mergePreviewLoading}
                  previewFailed={mergePreviewFailed}
                />
              </Box>
            ) : activeEdit.resource_type === 'funding_round' &&
              activeEdit.action === 'delete' ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Round to delete
                </Typography>
                <RoundDeleteSummary item={activeEdit} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    {activeEdit.action === 'update' ? 'Changes' : 'New record'}
                  </Typography>
                  {activeEdit.status === 'pending' &&
                    amendingId !== activeEdit.id && (
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => startAmend(activeEdit)}
                      >
                        {activeEdit.resource_type === 'opportunity_questions'
                          ? 'Edit questions'
                          : 'Amend'}
                      </Button>
                    )}
                </Box>
                {amendingId === activeEdit.id ? (
                  (() => {
                    const amendErrors = validatePayload(
                      activeEdit.resource_type,
                      amendDraft,
                    )
                    const hasErrors = Object.keys(amendErrors).length > 0
                    return (
                      <Box
                        sx={{
                          p: 1.5,
                          border: '1px dashed',
                          borderColor: 'primary.main',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 1, display: 'block' }}
                        >
                          Amending — edit values or drop fields you don&apos;t
                          want applied. Dropped fields will be left unchanged on
                          the target.
                        </Typography>
                        {activeEdit.resource_type ===
                        'opportunity_questions' ? (
                          <QuestionsAmendForm
                            value={amendDraft}
                            onChange={setAmendDraft}
                            currentRecord={currentRecord}
                            applicationLink={
                              typeof currentRecord?.application_link ===
                              'string'
                                ? currentRecord.application_link
                                : null
                            }
                          />
                        ) : (
                          <AmendForm
                            resource={activeEdit.resource_type as ResourceType}
                            value={amendDraft}
                            errors={amendErrors}
                            onChange={setAmendDraft}
                          />
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 1.5,
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            size="small"
                            onClick={cancelAmend}
                            disabled={amendSaving}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveAmend(activeEdit)}
                            disabled={amendSaving || hasErrors}
                          >
                            {amendSaving ? 'Saving…' : 'Save amendment'}
                          </Button>
                        </Box>
                      </Box>
                    )
                  })()
                ) : activeEdit.resource_type === 'opportunity_questions' ? (
                  <Box
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      This proposal will completely replace all structured
                      questions for the opportunity. Any edit below starts an
                      amendment that must be saved before approving.
                    </Typography>
                    {activeEdit.status === 'pending' ? (
                      <QuestionsAmendForm
                        value={activeEdit.payload}
                        onChange={(next) => {
                          setAmendingId(activeEdit.id)
                          setAmendDraft(next)
                        }}
                        currentRecord={currentRecord}
                        applicationLink={
                          typeof currentRecord?.application_link === 'string'
                            ? currentRecord.application_link
                            : null
                        }
                      />
                    ) : (
                      <QuestionsPreview
                        questions={questionsFromPayload(activeEdit.payload)}
                      />
                    )}
                  </Box>
                ) : activeEdit.action === 'update' &&
                  (loadingCurrent || loadingCurrentRegions) ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '22%' }}>Field</TableCell>
                          {activeEdit.action === 'update' && (
                            <TableCell sx={{ width: '39%' }}>
                              {diffColumnLabels(activeEdit.status).before}
                            </TableCell>
                          )}
                          <TableCell>
                            {diffColumnLabels(activeEdit.status).after}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diffRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={activeEdit.action === 'update' ? 3 : 2}
                              align="center"
                            >
                              No fields to display
                            </TableCell>
                          </TableRow>
                        ) : (
                          diffRows.map((row) => (
                            <TableRow
                              key={row.key}
                              sx={{
                                bgcolor: row.changed ? 'warning.50' : undefined,
                              }}
                            >
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {row.label}
                              </TableCell>
                              {activeEdit.action === 'update' && (
                                <TableCell
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  {renderDiffCell(
                                    row.before,
                                    row.after,
                                    'before',
                                    row.changed,
                                    row.key === 'region_ids'
                                      ? regionDiffFormatter
                                      : fieldDefFor(
                                          activeEdit.resource_type,
                                          row.key,
                                        ).type === 'date'
                                      ? formatLongDateNZ
                                      : undefined,
                                    fieldDefFor(
                                      activeEdit.resource_type,
                                      row.key,
                                    ).type === 'url',
                                  )}
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  verticalAlign: 'top',
                                }}
                              >
                                {renderDiffCell(
                                  row.before,
                                  row.after,
                                  'after',
                                  row.changed,
                                  row.key === 'region_ids'
                                    ? regionDiffFormatter
                                    : fieldDefFor(
                                        activeEdit.resource_type,
                                        row.key,
                                      ).type === 'date'
                                    ? formatLongDateNZ
                                    : undefined,
                                  fieldDefFor(activeEdit.resource_type, row.key)
                                    .type === 'url',
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* Regen panel for opportunity edits touching profile-driver
                fields — only useful pre-approval (fs-3oh). Condition reuses the
                oppRegenWarranted derivation so the panel and the P1 approve-gate
                are exactly congruent. */}
            {activeEdit.status === 'pending' &&
              oppRegenWarranted &&
              activeEdit.target_uuid && (
                <RegenPanel
                  key={activeEdit.id}
                  targetUuid={activeEdit.target_uuid}
                  mergedDescription={String(
                    activeEdit.payload.description ??
                      currentRecord?.description ??
                      '',
                  )}
                  mergedCriteria={String(
                    activeEdit.payload.criteria ??
                      currentRecord?.criteria ??
                      '',
                  )}
                  mergedExclusions={String(
                    activeEdit.payload.exclusions ??
                      currentRecord?.exclusions ??
                      '',
                  )}
                  mergedFundName={String(
                    activeEdit.payload.fund_name ??
                      currentRecord?.fund_name ??
                      '',
                  )}
                  currentProfileText={String(currentRecord?.profile_text ?? '')}
                  currentTags={
                    Array.isArray(currentRecord?.tags)
                      ? (currentRecord.tags as string[])
                      : []
                  }
                  proposedEditId={activeEdit.id}
                  currentPayload={activeEdit.payload}
                  onUnsavedChange={setRegenUnsaved}
                  onCompletedChange={handleRegenCompleted}
                />
              )}

            {/* Fan-out regen panel for funder edits touching description
                — only useful pre-approval (fs-3oh). Condition reuses
                funderRegenWarranted for gate/panel congruence. */}
            {activeEdit.status === 'pending' &&
              funderRegenWarranted &&
              activeEdit.target_uuid && (
                <FunderRegenPanel
                  key={activeEdit.id}
                  targetUuid={activeEdit.target_uuid}
                  mergedDescription={String(
                    activeEdit.payload.description ??
                      currentRecord?.description ??
                      '',
                  )}
                  currentFunderDescription={String(
                    currentRecord?.description ?? '',
                  )}
                  proposedEditId={activeEdit.id}
                  onUnsavedChange={setRegenUnsaved}
                  onCompletedChange={handleRegenCompleted}
                />
              )}

            {activeEdit.reviewed_at || activeEdit.status === 'withdrawn' ? (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2">
                  {activeEdit.status === 'withdrawn' ? 'Withdrawn' : 'Reviewed'}
                </Typography>
                {activeEdit.status === 'withdrawn' ? (
                  <Typography variant="body2">
                    {activeEdit.withdrawn_at ?? 'withdrawn'}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    {activeEdit.reviewed_at} by{' '}
                    {activeEdit.reviewed_by_name ??
                      activeEdit.reviewed_by_auth0_id}
                  </Typography>
                )}
                {activeEdit.review_comment && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {activeEdit.review_comment}
                  </Typography>
                )}
                {activeEdit.withdraw_reason && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Withdrawal reason: {activeEdit.withdraw_reason}
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <TextField
                  label="Review comment (optional)"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mt: 3 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap' }}>
            {/* GH#1568: an open amendment is an unresolved sub-workflow. Block
                the headline Approve/Reject until it's saved or cancelled so a
                stray click can't apply the un-amended payload and discard the
                reviewer's in-progress edit. */}
            {activeEdit.status === 'pending' && amendingId !== null && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ mr: 'auto', pl: 1 }}
              >
                Finish or cancel the amendment before reviewing.
              </Typography>
            )}
            {/* GH#1630: unsaved profile/tag regeneration is the same
                modal-inside-a-modal trap — block the headline actions until the
                reviewer saves or discards the generated output. */}
            {activeEdit.status === 'pending' &&
              amendingId === null &&
              regenUnsaved && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ mr: 'auto', pl: 1 }}
                >
                  Save or discard the regenerated profile before reviewing.
                </Typography>
              )}
            {/* GH#1849 P1: this edit's diff warrants a profile regen. Approve is
                gated from the outset — the reviewer must regenerate-and-save or
                explicitly skip. Shown only when there's no unsaved-regen banner
                already up. */}
            {activeEdit.status === 'pending' &&
              amendingId === null &&
              !regenUnsaved &&
              regenGateActive && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ mr: 'auto', pl: 1 }}
                >
                  Regenerate the profile and save, or skip regeneration, before
                  approving.
                </Typography>
              )}
            <Button onClick={onClose} disabled={reviewing}>
              Close
            </Button>
            {activeEdit.status === 'pending' && (
              <>
                {regenGateActive && (
                  <Button
                    color="warning"
                    variant="outlined"
                    onClick={() => setRegenSkippedKey(gateKey)}
                    disabled={reviewing || amendingId !== null}
                  >
                    Skip profile regeneration
                  </Button>
                )}
                <Button
                  color="error"
                  onClick={() => review('reject')}
                  disabled={reviewing || amendingId !== null || regenUnsaved}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => review('approve')}
                  disabled={
                    reviewing ||
                    approvalConflicts.length > 0 ||
                    amendingId !== null ||
                    regenUnsaved ||
                    approveBlockedByRegen ||
                    mergeApprovalBlocked
                  }
                >
                  Approve
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
